import { useState } from "react";
import { Sparkles, X, Maximize2, MessageCircle, ExternalLink, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useLocation, Link } from "react-router-dom";
import SupportAssistantPanel from "./SupportAssistantPanel";
import { cn } from "@/lib/utils";

const HIDDEN_ROUTES = ["/share"];
const PAGE_ID = "100064301193320";
const GREETING = "اهلا بحضرتك .. فى اى استفسار او اسئله ؟";

export default function SupportAssistantBubble() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"assistant" | "human">("human");

  if (HIDDEN_ROUTES.some((p) => loc.pathname.startsWith(p))) return null;
  if (loc.pathname === "/support") return null;

  const messengerUrl = `https://m.me/${PAGE_ID}?ref=website_chat`;
  const isRTL = i18n.language === "ar";

  const openMessenger = () => {
    const w = window.screen?.width ?? 1200;
    const h = window.screen?.height ?? 800;
    const width = 420;
    const height = 640;
    const left = Math.max(0, Math.round((w - width) / 2));
    const top = Math.max(0, Math.round((h - height) / 2));
    const features = `popup=yes,width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`;
    const win = window.open(messengerUrl, "commentsbot_messenger", features);
    if (!win) window.open(messengerUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      {open && (
        <div className={cn("fixed bottom-24 z-50 w-[380px] max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl overflow-hidden border border-border bg-card animate-fade-in", isRTL ? "left-6" : "right-6")}>
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {tab === "assistant" ? <Sparkles className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
              <span className="font-semibold text-sm">
                {tab === "assistant" ? t("supportChat.title") : t("chatWidget.chatWithUs")}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {tab === "assistant" && user && (
                <Link
                  to="/support"
                  onClick={() => setOpen(false)}
                  className="hover:opacity-80 p-1"
                  aria-label={t("supportChat.expand")}
                >
                  <Maximize2 className="h-4 w-4" />
                </Link>
              )}
              <button
                onClick={() => setOpen(false)}
                className="hover:opacity-80 p-1"
                aria-label={t("common.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="grid grid-cols-2 border-b border-border bg-muted/40 text-xs font-medium">
            <button
              onClick={() => setTab("assistant")}
              className={cn(
                "py-2 flex items-center justify-center gap-1.5 transition-colors",
                tab === "assistant"
                  ? "bg-card text-foreground border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Sparkles className="h-3.5 w-3.5" />
              {t("supportChat.tabAssistant")}
            </button>
            <button
              onClick={() => setTab("human")}
              className={cn(
                "py-2 flex items-center justify-center gap-1.5 transition-colors",
                tab === "human"
                  ? "bg-card text-foreground border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <MessageCircle className="h-3.5 w-3.5" />
              {t("supportChat.tabHuman")}
            </button>
          </div>

          {/* Body */}
          {tab === "assistant" ? (
            user ? (
              <div className="p-3">
                <SupportAssistantPanel height="h-[440px]" compact />
              </div>
            ) : (
              <div className="p-6 text-center space-y-3">
                <Sparkles className="h-8 w-8 mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">{t("supportChat.signInForAssistant")}</p>
                <Link
                  to="/auth"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90"
                >
                  {t("supportChat.signIn")}
                </Link>
                <button
                  onClick={() => setTab("human")}
                  className="block w-full text-xs text-primary hover:underline"
                >
                  {t("supportChat.switchToHuman")}
                </button>
              </div>
            )
          ) : (
            <div className="p-4 space-y-4">
              {/* Greeting bubble */}
              <div className="flex gap-2 items-start">
                <div className="h-8 w-8 rounded-full bg-[#0084FF] text-white flex items-center justify-center shrink-0 shadow-sm">
                  <MessageCircle className="h-4 w-4" />
                </div>
                <div className="rounded-2xl rounded-tl-sm bg-muted px-3 py-2 max-w-[85%]">
                  <p
                    className={cn("text-sm text-foreground leading-relaxed", isRTL ? "text-right" : "text-left")}
                    dir="rtl"
                  >
                    {GREETING}
                  </p>
                </div>
              </div>

              {/* Response time note */}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{t("supportChat.humanReplyTime", "Typically replies within a few hours")}</span>
              </div>

              {/* Open Messenger CTA */}
              <button
                onClick={openMessenger}
                className="flex items-center justify-center gap-2 w-full rounded-lg bg-[#0084FF] hover:bg-[#0073e6] text-white py-2.5 px-4 text-sm font-semibold transition-colors shadow-sm"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
                  <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.907 1.194 5.418 3.14 7.123V22l3.476-1.91c.927.256 1.91.396 2.934.396h.05c5.473 0 9.95-4.095 9.95-9.243S17.523 2 12 2zm1.062 12.443-2.538-2.707-4.95 2.707 5.444-5.78 2.6 2.707 4.888-2.707-5.444 5.78z" />
                </svg>
                {t("chatWidget.openMessenger")}
                <ExternalLink className="h-3.5 w-3.5 opacity-70" />
              </button>

              <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                {t("supportChat.humanFootnote", "Opens Facebook Messenger in a new window.")}
              </p>
            </div>
          )}
        </div>
      )}

      {!open && (
        <span className={cn("fixed bottom-8 z-50 hidden sm:flex items-center gap-1.5 rounded-full bg-card border border-border shadow-md px-3 py-1.5 text-xs font-medium text-foreground animate-fade-in pointer-events-none", isRTL ? "left-24" : "right-24")}>
          {t("supportChat.bubbleHint", "Chat with us")}
          <span className={cn("absolute top-1/2 -translate-y-1/2 h-3 w-3 rotate-45 bg-card", isRTL ? "-left-1.5 border-l border-b border-border" : "-right-1.5 border-r border-t border-border")} />
        </span>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn("fixed bottom-6 z-50 h-16 w-16 rounded-full gradient-primary text-primary-foreground shadow-xl flex items-center justify-center transition-transform hover:scale-105 ring-4 ring-primary/20", isRTL ? "left-6" : "right-6")}
        aria-label={t("supportChat.title")}
      >
        {open ? (
          <X className="h-6 w-6" />
        ) : (
          <span className="relative flex items-center justify-center">
            <MessageCircle className="h-7 w-7" fill="currentColor" />
            <span className={cn("absolute -top-1 h-3 w-3 rounded-full bg-green-500 border-2 border-primary animate-pulse", isRTL ? "-left-1" : "-right-1")} />
          </span>
        )}
      </button>
    </>
  );
}
